import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Broom, CheckCircle, X, ArrowClockwise, Warning } from "@phosphor-icons/react";

const AGES = [
  { label: "> 7 days", value: 7 },
  { label: "> 14 days", value: 14 },
  { label: "> 30 days", value: 30 },
  { label: "> 90 days", value: 90 },
];

const statusColor = (s) => ({
  new: "bg-blue-100 text-blue-800",
  pool: "bg-slate-100 text-slate-800",
  unassigned: "bg-slate-100 text-slate-800",
  assigned: "bg-amber-100 text-amber-800",
  in_progress: "bg-cyan-100 text-cyan-800",
}[s] || "bg-slate-100 text-slate-800");

export default function AdminBacklog() {
  const [days, setDays] = useState(7);
  const [status, setStatus] = useState("");
  const [assignee, setAssignee] = useState("");
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [workers, setWorkers] = useState([]);

  const load = async () => {
    setChecked(new Set());
    try {
      const params = new URLSearchParams({ days_old: String(days) });
      if (status) params.set("status", status);
      if (assignee) params.set("assignee", assignee);
      const r = await api.get(`/backlog/jobs?${params}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load backlog");
    }
  };
  useEffect(() => { load(); }, [days, status, assignee]);
  useEffect(() => {
    api.get("/admins/workers").then(r => setWorkers(r.data?.workers || r.data || [])).catch(() => {});
  }, []);

  const toggleAll = () => {
    if (!data) return;
    if (checked.size === data.jobs.length) setChecked(new Set());
    else setChecked(new Set(data.jobs.map(j => j.id)));
  };

  const bulk = async (action, extras = {}) => {
    if (checked.size === 0) return toast.error("Select at least one WO");
    const notes = extras.needsNotes
      ? window.prompt(extras.prompt || "Add a note (optional):", "") || ""
      : "";
    const body = { job_ids: [...checked], action, notes };
    if (extras.reassignTo) {
      body.reassign_to = extras.reassignTo.id;
      body.reassign_to_name = extras.reassignTo.name;
    }
    try {
      const r = await api.post("/backlog/jobs/bulk-action", body);
      toast.success(`${action.replace("_", " ")} · ${r.data.updated} WO${r.data.updated === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk action failed");
    }
  };

  const grouped = useMemo(() => {
    if (!data) return [];
    return data.jobs;
  }, [data]);

  return (
    <AdminShell>
      <div className="max-w-7xl mx-auto p-4 space-y-4" data-testid="backlog-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-red-600">Health Score Cleanup</div>
            <h1 className="font-display font-black text-3xl uppercase tracking-tight flex items-center gap-2">
              <Broom weight="fill" size={28}/> Backlog Buster
            </h1>
            <div className="text-xs text-slate-500 mt-1">Every WO that's been sitting open longer than it should. Bulk close, cancel, or reassign — clear the drag on your Portfolio Health Score.</div>
          </div>
          <button onClick={load}
            className="h-10 px-3 border-2 border-slate-800 bg-white hover:bg-slate-100 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
            <ArrowClockwise size={14}/> Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="p-3 bg-white border-2 border-slate-300 flex items-center gap-2 flex-wrap">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Older than</div>
          <select value={days} onChange={e => setDays(parseInt(e.target.value))}
            data-testid="backlog-age-filter"
            className="h-9 px-2 border-2 border-slate-300 text-xs font-black uppercase">
            {AGES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Status</div>
          <select value={status} onChange={e => setStatus(e.target.value)}
            className="h-9 px-2 border-2 border-slate-300 text-xs font-black uppercase">
            <option value="">Any open</option>
            <option value="new">New</option>
            <option value="pool">Pool</option>
            <option value="assigned">Assigned</option>
            <option value="in_progress">In Progress</option>
          </select>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Assignee</div>
          <select value={assignee} onChange={e => setAssignee(e.target.value)}
            className="h-9 px-2 border-2 border-slate-300 text-xs">
            <option value="">Any</option>
            <option value="unassigned">— Unassigned —</option>
            {(data?.assignees || []).map(a => (
              <option key={a.name} value={a.name}>{a.name} ({a.count})</option>
            ))}
          </select>
          {data && (
            <span className="ml-auto text-sm">
              <span className="font-display font-black text-2xl text-red-700">{data.count}</span>
              <span className="text-slate-500 text-xs uppercase tracking-widest font-black ml-1">stale WOs</span>
            </span>
          )}
        </div>

        {/* Bulk action bar */}
        <div className="p-3 bg-red-50 border-2 border-red-500 flex items-center gap-2 flex-wrap sticky top-0 z-10 shadow-[3px_3px_0_#dc2626]">
          <div className="text-xs font-black uppercase tracking-widest text-red-800">
            {checked.size} selected
          </div>
          <button onClick={toggleAll} data-testid="backlog-select-all"
            className="h-8 px-2 border border-slate-500 bg-white hover:bg-slate-100 text-[10px] font-black uppercase tracking-widest">
            {data && checked.size === data.jobs.length ? "Clear all" : "Select all"}
          </button>
          <button onClick={() => bulk("close_completed", { needsNotes: true, prompt: "Optional close note:" })}
            disabled={checked.size === 0}
            data-testid="backlog-bulk-close"
            className="h-8 px-3 border-2 border-emerald-700 bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
            <CheckCircle size={12} weight="fill"/> Close as Completed
          </button>
          <button onClick={() => bulk("cancel", { needsNotes: true, prompt: "Why cancel? (e.g. tenant left, duplicate, no-show)" })}
            disabled={checked.size === 0}
            data-testid="backlog-bulk-cancel"
            className="h-8 px-3 border-2 border-slate-700 bg-slate-700 hover:bg-slate-600 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
            <X size={12} weight="bold"/> Cancel
          </button>
          <button onClick={() => bulk("mark_follow_up", { needsNotes: true, prompt: "Follow-up note (optional):" })}
            disabled={checked.size === 0}
            className="h-8 px-3 border-2 border-amber-700 bg-amber-500 hover:bg-amber-400 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
            Follow-Up
          </button>
          <select onChange={e => {
              const w = workers.find(x => (x.id || x._id) === e.target.value);
              if (!w) return;
              bulk("reassign", { reassignTo: { id: w.id || w._id, name: w.name } });
              e.target.value = "";
            }}
            defaultValue=""
            disabled={checked.size === 0}
            data-testid="backlog-bulk-reassign"
            className="h-8 px-2 border-2 border-cyan-700 bg-cyan-600 hover:bg-cyan-500 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-40">
            <option value="" disabled>REASSIGN TO…</option>
            {workers.map(w => (
              <option key={w.id || w._id} value={w.id || w._id} className="text-slate-900">{w.name}</option>
            ))}
          </select>
        </div>

        {/* Job list */}
        <div className="bg-white border-2 border-slate-300">
          {!data && <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>}
          {data && data.jobs.length === 0 && (
            <div className="p-8 text-center">
              <CheckCircle weight="fill" size={32} className="mx-auto text-emerald-500"/>
              <div className="mt-2 font-black uppercase text-xs tracking-widest text-emerald-700">Nothing stale under this filter</div>
              <div className="text-[11px] text-slate-500 mt-1">Great job. Move on to the next thing.</div>
            </div>
          )}
          <div className="divide-y divide-slate-100" data-testid="backlog-list">
            {grouped.map(j => {
              const on = checked.has(j.id);
              return (
                <label key={j.id} className={`flex items-start gap-2 p-2 hover:bg-slate-50 cursor-pointer ${on ? "bg-red-50" : ""}`}>
                  <input type="checkbox" checked={on}
                    onChange={() => {
                      const s = new Set(checked);
                      s.has(j.id) ? s.delete(j.id) : s.add(j.id);
                      setChecked(s);
                    }}
                    className="mt-1"/>
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="font-mono text-[10px] text-slate-500">#{j.work_order_number || j.id.slice(-6)}</span>
                      <span className={`px-1 py-0.5 text-[9px] font-black uppercase tracking-widest ${statusColor(j.status)}`}>{j.status}</span>
                      <span className={`px-1 py-0.5 text-[9px] font-black uppercase tracking-widest ${j.age_days > 30 ? "bg-red-200 text-red-800" : j.age_days > 14 ? "bg-amber-200 text-amber-800" : "bg-slate-200 text-slate-700"}`}>
                        {j.age_days}d old
                      </span>
                      <Link to={`/admin/jobs/${j.id}`} className="ml-auto text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:text-cyan-900">Open →</Link>
                    </div>
                    <div className="font-black text-slate-900 truncate">{j.title || "(no title)"}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {j.property_address}{j.unit ? ` · ${j.unit}` : ""} · {j.assigned_to_name}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
